"use client";

import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { IconButton } from "@ecco/ui/icon-button";
import { CarouselSlider } from "@ecco/ui/carousel";
import { ChevronLeftIcon, ChevronRightIcon } from "@ecco/ui/carousel-icons";
import { useNavigationControls } from "@ecco/ui/use-navigation-controls";
import { useSlidesPerView } from "@ecco/ui/use-slides-per-view";
import { headingSizeMap, mapSizesTitle } from "@ecco/ui/map-sizes";
import { useBoundingRect } from "@ecco/ui/use-bounding-rect";
import { useIsomorphicLayoutEffect } from "@ecco/ui/use-isomorphic-layout-effect";
import { Children, useEffect, useState } from "react";
import type { FC, ReactElement, ReactNode } from "react";
import { styles } from "./StoryTileCarouselStyles";

type StoryTileCarouselProps = {
    title?: string;
    titleSize?: string;
    children?: ReactNode;
};

const GAP = 12;
const SLIDES_PER_VIEW = [1.2, 2.2, 3.2];
const DESKTOP_TILE_HORIZONTAL_PADDING = 8;
const MOBILE_DESIGN_VIEWPORT = 390;
const MOBILE_DESIGN_PADDING = 32;
const MOBILE_DESIGN_IMAGE_WIDTH = 302.66;

const getSlideWidth = (
    sliderNode: HTMLDivElement,
    containerWidth: number,
    slidesPerView: number
): number => {
    const style = window.getComputedStyle(sliderNode);
    const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const contentWidth = containerWidth - paddingX;

    if (slidesPerView < 2) {
        const designContentWidth = MOBILE_DESIGN_VIEWPORT - MOBILE_DESIGN_PADDING;
        return (contentWidth / designContentWidth) * MOBILE_DESIGN_IMAGE_WIDTH;
    }

    return contentWidth / slidesPerView - GAP + DESKTOP_TILE_HORIZONTAL_PADDING;
};

export const StoryTileCarousel: FC<StoryTileCarouselProps> = ({
    title,
    titleSize = "large",
    children,
}) => {
    const { slidesPerView } = useSlidesPerView(SLIDES_PER_VIEW);
    const arrayChildren = Children.toArray(children);
    const { ref: sliderRef, dimensions, node: sliderNode } = useBoundingRect<HTMLDivElement>();
    const [slideWidth, setSlideWidth] = useState(0);
    const resolvedTitleSize = titleSize in mapSizesTitle ? titleSize : "large";

    useIsomorphicLayoutEffect(() => {
        if (!sliderNode || !dimensions.width) {
            setSlideWidth(0);
            return;
        }

        setSlideWidth(getSlideWidth(sliderNode, dimensions.width, slidesPerView));
        sliderNode.scrollBy(0, 0);
    }, [sliderNode, dimensions.width, slidesPerView, arrayChildren.length]);

    const scrollStep = slideWidth ? slideWidth + GAP : 0;
    const { scrollToNextSlide, scrollToPrevSlide, isNextDisabled, isPreviousDisabled } =
        useNavigationControls(sliderNode, scrollStep, arrayChildren as ReactElement[]);

    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        if (!sliderNode || !slideWidth) return;

        const handleScroll = () => {
            setCurrentPage(Math.round(sliderNode.scrollLeft / (slideWidth + GAP)));
        };

        sliderNode.addEventListener("scroll", handleScroll);

        return () => sliderNode.removeEventListener("scroll", handleScroll);
    }, [sliderNode, slideWidth]);

    const showNavigation = arrayChildren.length > slidesPerView;
    const totalDots = Math.max(1, arrayChildren.length - Math.floor(slidesPerView) + 1);

    return (
        <Box {...styles.container}>
            <Flex {...styles.header} justifyContent={title ? "space-between" : "flex-end"}>
                {title && (
                    <Box
                        as={headingSizeMap[resolvedTitleSize]}
                        fontSize={mapSizesTitle[resolvedTitleSize]}
                        aria-label={title}
                        {...styles.title}
                    >
                        {title}
                    </Box>
                )}
                {showNavigation && (
                    <Flex {...styles.navContainer}>
                        <Flex {...styles.navRow}>
                            <Flex {...styles.navButtons}>
                                <IconButton
                                    {...styles.navButton}
                                    {...styles.navButtonPrev}
                                    onClick={scrollToPrevSlide}
                                    variant="tertiary"
                                    aria-label="Previous slide"
                                    disabled={isPreviousDisabled}
                                    data-testid="story-tile-carousel-prev"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <IconButton
                                    {...styles.navButton}
                                    onClick={scrollToNextSlide}
                                    variant="tertiary"
                                    aria-label="Next slide"
                                    disabled={isNextDisabled}
                                    data-testid="story-tile-carousel-next"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Flex>
                        </Flex>
                        <Flex {...styles.indicatorContainer}>
                            {Array.from({ length: totalDots }, (_, i) => (
                                <Box
                                    key={i}
                                    {...(i === currentPage
                                        ? styles.indicatorActive
                                        : styles.indicatorDot)}
                                />
                            ))}
                        </Flex>
                    </Flex>
                )}
            </Flex>
            <CarouselSlider
                ref={sliderRef}
                gap={GAP}
                sx={{
                    ...styles.slider(GAP),
                    ...styles.sliderPseudo,
                    alignItems: "stretch",
                }}
            >
                {arrayChildren.map((child, i) => (
                    <Flex
                        key={i}
                        w={slideWidth ? `${slideWidth}px` : undefined}
                        minW={slideWidth ? `${slideWidth}px` : undefined}
                        maxW={slideWidth ? `${slideWidth}px` : undefined}
                        alignSelf="stretch"
                        flexDirection="column"
                        {...styles.tile}
                    >
                        {child}
                    </Flex>
                ))}
            </CarouselSlider>
        </Box>
    );
};
